import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Stack,
  ButtonGroup,
  Paper,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Table as CustomTable } from "app/components";
import { useDispatch, useSelector } from "react-redux";
import { getFlipkartPaymentList } from "app/redux/slice/flipkartSlice";
import { usePagination } from "app/hooks/usePagination";
import Loader from "@jumbo/components/Loader";
import moment from "moment";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const paymentModeOptions = [
  { value: "prepaid", label: "Prepaid", color: "success" },
  { value: "postpaid", label: "Postpaid", color: "warning" },
  { value: "part_payment", label: "Part Payment", color: "info" },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(amount || 0);

const EXCLUDED_KEYS = ["_id", "__v", "createdAt", "updatedAt", "deletedAt"];

const formatFieldLabel = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

const formatFieldValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return moment.utc(value).format("DD/MM/YYYY");
  }
  if (Array.isArray(value)) {
    return value.length ? JSON.stringify(value, null, 2) : "-";
  }
  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const formatReadable = (value) =>
  value
    ? value
        .toString()
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : "-";

const FlipkartPayments = () => {
  const dispatch = useDispatch();

  const flipkartData = useSelector((state) => state?.flipkart);
  const payments = flipkartData?.payments?.list ?? [];

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    modeOfPayment: "",
  });
  const [dateRange, setDateRange] = useState([null, null]);

  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [tempFilters, setTempFilters] = useState({
    modeOfPayment: "",
  });
  const [tempDateRange, setTempDateRange] = useState([null, null]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("orderDate");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "flipkart-payments-list" });

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, filters, dateRange, order, orderBy]);

  const fetchPayments = () => {
    const params = {
      page,
      perPage: rowsPerPage,
      search: searchTerm.trim(),
    };
    Object.entries(filters).forEach(([key, value]) => {
      if (key === "settlementStatus") return;
      if (value !== "" && value !== null && value !== undefined) {
        params[key] = value;
      }
    });
    if (dateRange[0] !== null && dateRange[1] !== null) {
      params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
      params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    }
    dispatch(getFlipkartPaymentList(params));
  };

  const applyFilters = () => {
    setSearchTerm(tempSearchTerm);
    setFilters({ ...tempFilters });
    setDateRange(tempDateRange);
    handleChangePage(0, 0);
  };

  const clearAll = () => {
    setTempSearchTerm("");
    setSearchTerm("");
    const cleared = {
      modeOfPayment: "",
    };
    setTempFilters(cleared);
    setFilters(cleared);
    setTempDateRange([null, null]);
    setDateRange([null, null]);
    handleChangePage(0, 0);
  };

  const getPaymentModeChip = (mode) => {
    const cfg = paymentModeOptions.find((o) => o.value === mode);
    return (
      <Chip
        label={cfg?.label || formatReadable(mode)}
        color={cfg?.color || "default"}
        size="small"
      />
    );
  };

  const columns = [
    { id: "orderId", label: "Order ID", sort: false },
    { id: "orderItemId", label: "Order Item ID", sort: false },
    {
      id: "skuName",
      label: "SKU",
      sort: false,
      render: (row) => (
        <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
          {row?.skuName || "-"}
        </Typography>
      ),
    },
    {
      id: "modeOfPayment",
      label: "Payment Mode",
      sort: false,
      render: (row) => getPaymentModeChip(row?.modeOfPayment),
    },
    {
      id: "projectedBankSettlement",
      label: "Projected Settlement",
      sort: false,
      isTotal: true,
      format: (v) => formatCurrency(v),
    },
    {
      id: "amountSettled",
      label: "Amount Settled",
      sort: false,
      isTotal: true,
      format: (v) => formatCurrency(v),
    },
    {
      id: "amountPending",
      label: "Amount Pending",
      sort: false,
      isTotal: true,
      format: (v) => formatCurrency(v),
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => (
        <IconButton
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            setSelectedPayment(row);
          }}
        >
          <MoreIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Flipkart Payment
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by order ID, item ID, SKU..."
                value={tempSearchTerm}
                onChange={(e) => setTempSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={tempDateRange}
                  onChange={setTempDateRange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={4}>
              <ButtonGroup size="small" fullWidth variant="outlined">
                {QUICK_RANGES.map((r) => (
                  <Button
                    key={r.key}
                    onClick={() => setTempDateRange(r.getRange())}
                  >
                    {r.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Mode</InputLabel>
                <Select
                  value={tempFilters.modeOfPayment}
                  onChange={(e) =>
                    setTempFilters((p) => ({
                      ...p,
                      modeOfPayment: e.target.value,
                    }))
                  }
                  label="Payment Mode"
                >
                  <MenuItem value="">All</MenuItem>
                  {paymentModeOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  fullWidth
                  size="small"
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearAll}
                  startIcon={<RefreshIcon />}
                  fullWidth
                  size="small"
                >
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {flipkartData?.payments?.loading ? (
        <Loader />
      ) : (
        <Card>
          <CardContent>
            <CustomTable
              columns={columns}
              rows={payments}
              page={page}
              rowsPerPage={rowsPerPage}
              count={flipkartData?.payments?.total}
              Order={order}
              OrderBy={orderBy}
              sortData={(o, ob) => {
                setOrder(o);
                setOrderBy(ob);
              }}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              loading={flipkartData?.payments?.loading}
              showTotalInFooter={true}
            />
          </CardContent>
        </Card>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            if (selectedPayment?._id) {
              setPaymentDetails(selectedPayment);
              setDetailsDialog(true);
            }
          }}
        >
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
      </Menu>

      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Payment Details</DialogTitle>
        <DialogContent dividers>
          {paymentDetails && (
            <Grid container spacing={2}>
              {Object.entries(paymentDetails)
                .filter(
                  ([key]) =>
                    !EXCLUDED_KEYS.includes(key) && key !== "transactions",
                )
                .map(([key, value], i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {formatFieldLabel(key)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {formatFieldValue(key, value)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
            </Grid>
          )}
          {paymentDetails?.transactions?.length > 0 ? (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, mb: 1.5, color: "primary.main" }}
              >
                Transactions
              </Typography>
              <Stack spacing={2}>
                {paymentDetails?.transactions?.map((txn, i) => (
                  <Paper
                    key={i}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderLeft: "4px solid",
                      borderLeftColor:
                        txn?.currentStatus === "Completed"
                          ? "success.main"
                          : txn?.currentStatus === "Pending"
                          ? "warning.main"
                          : txn?.currentStatus === "Failed"
                          ? "error.main"
                          : "grey.400",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {txn?.number || "Transaction"}
                      </Typography>
                      <Chip
                        label={txn?.currentStatus || "Unknown"}
                        size="small"
                        color={
                          txn?.currentStatus === "Completed"
                            ? "success"
                            : txn?.currentStatus === "Pending"
                            ? "warning"
                            : txn?.currentStatus === "Failed"
                            ? "error"
                            : "default"
                        }
                      />
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Grid container spacing={1.5}>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                          Amount
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          ₹{txn?.transactionAmount?.toFixed(2) ?? "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                          Payment Date
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {txn?.paymentDate
                            ? moment(txn.paymentDate).format("DD-MM-YYYY")
                            : "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                          Account Type
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {txn.accountType || "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          NEFT ID
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {txn.neftId || "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Reason
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {txn.reason || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            </Box>
          ) : (
            ""
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FlipkartPayments;
